import http from './http';

export function getAllInventoryUsers(page, search = '', limit = 15) {
    const params = { page, limit };

    if (search) {
        params.search = search;
    }

    return http.get(`/inventory/users`, { params });
}

//////////////////////////////////////////////////////////////////////////////////////////////////

export function findInventoryItem(id) {
    return http.get(`/inventory/items/${id}`);
}

export function paginateInventoryItems(page, search = '', limit = 15, query = {}) {
    const params = { page, limit, ...query };

    if (search) {
        params.search = search;
    }

    return http.get(`/inventory/items`, { params });
}

export function postPaginateInventoryItems(page, search = '', limit = 15, query = {}) {
    const params = { page, limit, ...query };

    if (search) {
        params.search = search;
    }

    return http.post(`/inventory/items/query`, params);
}

// Счётчики строки статусов. Считаются от той же выборки, что и список, но без фильтров,
// привязанных к статусу, — иначе соседние счётчики обнулялись бы при любом выборе.
export function getInventoryItemStats(query = {}, config = {}) {
    return http.post(`/inventory/items/stats`, query, config);
}

// Выгрузка приходит файлом, поэтому responseType: 'blob' — иначе axios попробует
// разобрать её как текст и испортит содержимое.
export function exportInventoryItems(query = {}) {
    return http.post(`/inventory/items/export`, query, { responseType: 'blob' });
}

export function createInventoryItems(items) {
    return http.post(`/inventory/items/bulk-insert`, items);
}

export function updateInventoryItem(item) {
    return http.put(`/inventory/items/${item.id}`, item);
}

export function getInventoryItemHistory(itemId, page = 1, perPage = 20) {
    return http.get(`/inventory/items/${itemId}/history`, { params: { page, per_page: perPage } });
}

/**
 * Деловая история предмета: что происходило с вещью.
 *
 * ОТДЕЛЬНО ОТ getInventoryItemHistory(), а не вместо неё. Та отдаёт технический журнал
 * правок («кто какое поле поменял») и нужна поддержке; эта — жизненный цикл («приняли,
 * выдали, увезли, списали»). Обе показываются во вкладке «История» одновременно.
 *
 * БЕЗ СТРАНИЦ: событий жизненного цикла у предмета единицы за всю его жизнь. Ответ
 * приезжает готовым к показу — имена держателей, названия городов и статусов уже
 * подставлены, и ходить за справочниками не нужно.
 *
 * @param {number} itemId
 * @returns {Promise}
 */
export function getInventoryItemLifecycle(itemId) {
    return http.get(`/inventory/items/${itemId}/lifecycle`);
}

// Вложения предмета: фотографии и документы. Ответ несёт и список (data), и ограничения
// загрузки (limits) — фронт не держит их вторым списком у себя.
// Обращения Service Desk, связанные с предметом (7-G1): только те, что пользователь
// может открыть. Только чтение — привязка делается из обращения.
export function getInventoryItemRequests(itemId) {
    return http.get(`/inventory/items/${itemId}/requests`);
}

export function getInventoryItemFiles(itemId) {
    return http.get(`/inventory/items/${itemId}/files`);
}

/**
 * Загрузить файл в хранилище.
 *
 * Общий загрузчик приложения, а не маршрут раздела: тот же, которым пользуются вложения
 * заявок. Прикрепление к предмету — следующий, отдельный запрос (attachInventoryItemFile):
 * два запроса вместо одного, зато один механизм записи в хранилище вместо двух.
 *
 * @param {File} file Файл из input.
 * @param {Function} onProgress Доля отправленного, 0…100. На телефоне это единственный
 *                              признак, что загрузка идёт, — фото уходит десятки секунд.
 * @returns {Promise} Ответ со списком созданных строк files.
 */
export function uploadFileToStorage(file, onProgress = null) {
    const payload = new FormData();
    payload.append('files', file);

    return http.post('/file/upload', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
            if (!onProgress || !event.total) {
                return;
            }

            onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
        },
    });
}

// Прикрепить уже загруженный файл к предмету. Категория и подпись — только здесь:
// общий загрузчик про оборудование ничего не знает.
export function attachInventoryItemFile(itemId, { fileId, category, comment }) {
    return http.post(`/inventory/items/${itemId}/files`, {
        file_id: fileId,
        category: category || null,
        comment: comment || null,
    });
}

// Удаляется ВЛОЖЕНИЕ (строка связи), поэтому в адресе его идентификатор, а не файла.
export function deleteInventoryItemFile(itemId, attachmentId) {
    return http.delete(`/inventory/items/${itemId}/files/${attachmentId}`);
}

/**
 * Назначить фотографию главной или снять признак.
 *
 * СВОЙ МАРШРУТ РАЗДЕЛА, а не общий файловый: у вложений оборудования открытого адреса нет
 * вовсе, и признак главного фото — состояние предмета, которое проверяется его правом
 * правки и городом. Адресуется ВЛОЖЕНИЕ (строка связи), как и удаление.
 *
 * @param {number} itemId
 * @param {number} attachmentId Идентификатор вложения, не файла.
 * @param {boolean} isPrimary
 * @returns {Promise}
 */
export function setInventoryItemFilePrimary(itemId, attachmentId, isPrimary) {
    return http.patch(`/inventory/items/${itemId}/files/${attachmentId}/primary`, {
        is_primary: Boolean(isPrimary),
    });
}

/**
 * Содержимое вложения.
 *
 * ЕДИНСТВЕННЫЙ ПУТЬ К ФАЙЛУ ОБОРУДОВАНИЯ: открытого адреса у него нет — маршрут стоит
 * под inventory:view с городской областью, и запрос идёт через http, то есть с токеном.
 * Отсюда и responseType: 'blob' — заголовок авторизации умеет отправить только запрос,
 * а <img src> и ссылка скачивания не умеют, поэтому кадр приходит содержимым и дальше
 * показывается через object URL.
 *
 * @param {number} itemId
 * @param {number} attachmentId Идентификатор вложения, не файла.
 * @returns {Promise}
 */
export function downloadInventoryItemFile(itemId, attachmentId) {
    return http.get(`/inventory/items/${itemId}/files/${attachmentId}/download`, { responseType: 'blob' });
}

/**
 * Бизнес-операции над оборудованием (7-F2A).
 *
 * ЯВНЫЕ ДЕЙСТВИЯ, А НЕ PATCH С ПОЛЕМ action: что именно произошло, видно уже по адресу.
 * Каждое меняет состояние предмета и пишет событие деловой истории ОДНОЙ транзакцией
 * под блокировкой строки.
 *
 * ТЕКУЩЕЕ СОСТОЯНИЕ НЕ ОТПРАВЛЯЕТСЯ. Ни держателя, ни статуса, ни города карточки
 * в теле нет: она могла быть открыта час назад, и решать по ней сервер не должен —
 * он читает состояние из базы уже под блокировкой. Отсюда и ответ: обновлённая карточка
 * целиком, чтобы панели не пришлось спрашивать её вторым запросом.
 *
 * @param {number} itemId
 * @param {{holderType: string, holderId: number, holderUntilAt?: string, comment: string}} payload
 * @returns {Promise}
 */
export function issueInventoryItem(itemId, { holderType, holderId, holderUntilAt, comment }) {
    return http.post(`/inventory/items/${itemId}/actions/issue`, {
        holder_type: holderType,
        holder_id: holderId,
        holder_until_at: holderUntilAt || null,
        comment: comment || null,
    });
}

/**
 * Вернуть оборудование на склад. От кого — сервер знает из самой карточки.
 *
 * @param {number} itemId
 * @param {{comment: string}} payload
 * @returns {Promise}
 */
export function returnInventoryItem(itemId, { comment } = {}) {
    return http.post(`/inventory/items/${itemId}/actions/return`, { comment: comment || null });
}

/**
 * Передать оборудование другому держателю, минуя склад.
 *
 * @param {number} itemId
 * @param {{holderType: string, holderId: number, holderUntilAt?: string, comment: string}} payload
 * @returns {Promise}
 */
export function changeInventoryItemOwner(itemId, { holderType, holderId, holderUntilAt, comment }) {
    return http.post(`/inventory/items/${itemId}/actions/change-holder`, {
        holder_type: holderType,
        holder_id: holderId,
        holder_until_at: holderUntilAt || null,
        comment: comment || null,
    });
}

/**
 * Перевезти оборудование в другой город.
 *
 * @param {number} itemId
 * @param {{cityId: number, comment: string}} payload
 * @returns {Promise}
 */
export function moveInventoryItem(itemId, { cityId, comment }) {
    return http.post(`/inventory/items/${itemId}/actions/move`, {
        city_id: cityId,
        comment: comment || null,
    });
}

/**
 * Отправить оборудование в ремонт (7-F2B).
 *
 * Описание неисправности обязательно — по нему ремонт потом и ищут. Держатель при этом
 * не меняется: он материально ответственный, а не тот, у кого вещь физически в руках.
 *
 * @param {number} itemId
 * @param {{comment: string}} payload
 * @returns {Promise}
 */
export function sendInventoryItemToRepair(itemId, { comment }) {
    return http.post(`/inventory/items/${itemId}/actions/send-to-repair`, { comment: comment || null });
}

/**
 * Вернуть оборудование из ремонта.
 *
 * Куда именно оно вернётся, решает сервер по держателю: держатель есть —
 * «В эксплуатации», держателя нет — «На складе». С формы это не приходит.
 *
 * @param {number} itemId
 * @param {{comment: string}} payload
 * @returns {Promise}
 */
export function returnInventoryItemFromRepair(itemId, { comment } = {}) {
    return http.post(`/inventory/items/${itemId}/actions/return-from-repair`, { comment: comment || null });
}

/**
 * Отметить оборудование неисправным (7-F2C).
 *
 * Описание обязательно. Держатель, город и плановые сроки не меняются: неисправность
 * не означает, что ответственный перестал отвечать за вещь.
 *
 * @param {number} itemId
 * @param {{comment: string}} payload
 * @returns {Promise}
 */
export function markInventoryItemBroken(itemId, { comment }) {
    return http.post(`/inventory/items/${itemId}/actions/mark-broken`, { comment: comment || null });
}

/**
 * Снять отметку о неисправности.
 *
 * Куда вернётся оборудование — «в эксплуатацию» или «на склад», — решает сервер
 * по держателю; с формы это не приходит.
 *
 * @param {number} itemId
 * @param {{comment: string}} payload
 * @returns {Promise}
 */
export function clearInventoryItemFault(itemId, { comment } = {}) {
    return http.post(`/inventory/items/${itemId}/actions/clear-fault`, { comment: comment || null });
}

/**
 * Списать оборудование.
 *
 * Причина обязательна: списание — последняя запись в жизни вещи, и именно к ней
 * возвращаются при инвентаризации. Удаления не происходит, в том числе мягкого.
 *
 * @param {number} itemId
 * @param {{comment: string}} payload
 * @returns {Promise}
 */
export function writeOffInventoryItem(itemId, { comment }) {
    return http.post(`/inventory/items/${itemId}/actions/write-off`, { comment: comment || null });
}

// ПРЯМЫХ ВЫЗОВОВ СТАТУСА И ДЕРЖАТЕЛЯ ЗДЕСЬ БОЛЬШЕ НЕТ. Были changeInventoryItemStatus()
// (PATCH /items/{id}/status) и changeInventoryItemHolder() (PATCH /items/{id}/holder);
// оба маршрута закрыты — держатель в 7-F2B, статус в 7-F2C — и отвечают 422 с указанием,
// каким действием пользоваться вместо. Оставленная здесь функция была бы ловушкой:
// вызвать её можно, а сработать она не может.
//
// Состояние оборудования меняют только действия жизненного цикла выше: issue, return,
// changeOwner, move, sendToRepair, returnFromRepair, markBroken, clearFault, writeOff.

//////////////////////////////////////////////////////////////////////////////////////////////////

export function findInventoryHolder(id) {
    return http.get(`/inventory/holders/${id}`);
}

export function paginateInventoryHolders(page = 1, search = '', query = {}) {
    const params = { page, ...query };

    if (search) {
        params.search = search;
    }

    return http.get(`/inventory/holders`, { params });
}

export function createInventoryHolder(holder) {
    return http.post(`/inventory/holders`, holder);
}

// Удаления у справочника нет: гашение — это обычное обновление с active = false.
export function updateInventoryHolder(holder) {
    return http.put(`/inventory/holders/${holder.id}`, holder);
}

//////////////////////////////////////////////////////////////////////////////////////////////////

export function findInventoryAttribute(id) {
    return http.get(`/inventory/attributes/${id}`);
}

export function paginateInventoryAttributes(page, search = '', limit = 15, query = null) {
    const params = { page, limit, ...query };

    if (search) {
        params.search = search;
    }

    return http.get(`/inventory/attributes`, { params });
}

/**
 * Справочники раздела: города и юр. лица.
 *
 * Отдельный контракт модуля, а не общий /v1/source-data приложения и не /v1/cities:
 * формы инвентаря берут города только отсюда, чтобы источник был один.
 */
export function getInventorySourceData() {
    return http.get(`/inventory/source-data`);
}

export function getInventoryAttributeTypes(query = {}) {
    const params = query;

    return http.get(`/inventory/attributes/types`, { params });
}

export function createInventoryAttribute(item) {
    return http.post(`/inventory/attributes`, item);
}

export function createInventoryAttributes(items) {
    return http.post(`/inventory/attributes/bulk-insert`, items);
}

export function updateInventoryAttribute(item) {
    return http.put(`/inventory/attributes/${item.id}`, item);
}

/**
 * Удалить значение справочника.
 *
 * Занятое значение сервер не удаляет и отвечает 422 с числом ссылок
 * (InventoryAttributeService::delete). Интерфейс прячет действие заранее — по usage_count
 * из списка, — но решение остаётся за сервером: список мог устареть за время работы.
 *
 * @param {number} id
 * @returns {Promise}
 */
export function deleteInventoryAttribute(id) {
    return http.delete(`/inventory/attributes/${id}`);
}

//////////////////////////////////////////////////////////////////////////////////////////////////

export function findInventoryPreset(id) {
    return http.get(`/inventory/presets/${id}`);
}

export function paginateInventoryPresets(page, search = '', limit = 15, query = {}) {
    const params = { page, limit, ...query };

    if (search) {
        params.search = search;
    }

    return http.get(`/inventory/presets`, { params });
}

export function createInventoryPreset(preset) {
    return http.post(`/inventory/presets`, preset);
}

export function updateInventoryPreset(item) {
    return http.put(`/inventory/presets/${item.id}`, item);
}

/**
 * Удалить шаблон.
 *
 * Шаблон участвует только при создании оборудования и копируется в новый предмет
 * целиком; после создания предмет с ним не связан. Поэтому здесь настоящее удаление,
 * а не гашение — в отличие от держателей.
 *
 * @param {number} id
 * @returns {Promise}
 */
export function deleteInventoryPreset(id) {
    return http.delete(`/inventory/presets/${id}`);
}

/**
 * Содержимое типового изображения модели.
 *
 * ЕДИНСТВЕННЫЙ ПУТЬ К КАРТИНКЕ ШАБЛОНА: открытого адреса у неё нет — маршрут стоит
 * под inventory:view, и запрос идёт через http, то есть с токеном. Отсюда и
 * responseType: 'blob' — заголовок авторизации умеет отправить только запрос, а
 * <img src> не умеет, поэтому кадр приходит содержимым и показывается через object URL.
 * Тот же порядок, что у вложений оборудования.
 *
 * @param {number} presetId
 * @returns {Promise}
 */
export function downloadInventoryPresetPreview(presetId) {
    return http.get(`/inventory/presets/${presetId}/preview`, { responseType: 'blob' });
}

/**
 * Поставить шаблону типовое изображение или заменить прежнее.
 *
 * Файл кладёт в хранилище общий загрузчик (uploadFileToStorage), сюда приходит уже
 * загруженный file_id — два запроса вместо одного, зато один механизм записи в хранилище.
 * Один маршрут и на «добавить», и на «заменить»: у шаблона ровно одна картинка.
 *
 * @param {number} presetId
 * @param {number} fileId
 * @returns {Promise}
 */
export function setInventoryPresetPreview(presetId, fileId) {
    return http.post(`/inventory/presets/${presetId}/preview`, { file_id: fileId });
}

/**
 * Убрать типовое изображение.
 *
 * @param {number} presetId
 * @returns {Promise}
 */
export function deleteInventoryPresetPreview(presetId) {
    return http.delete(`/inventory/presets/${presetId}/preview`);
}

/**
 * Добавить шаблон в избранное или убрать из него.
 *
 * Избранное персональное: сервер берёт пользователя из токена, идентификатор учётки
 * в запрос не кладётся и класть его нельзя — иначе избранное можно было бы менять чужое.
 *
 * @param {number} id
 * @param {boolean} favorite
 * @returns {Promise}
 */
export function setInventoryPresetFavorite(id, favorite) {
    return http.put(`/inventory/presets/${id}/favorite`, { favorite: Boolean(favorite) });
}
